/**
 * Esquema de la base de datos local.
 *
 * IndexedDB a traves de Dexie. No hay servidor ni base de datos remota: todos
 * los datos viven en este dispositivo (Req. 1.1, 1.2, 1.3).
 *
 * Se eligio IndexedDB y no localStorage porque localStorage tiene limite de
 * unos 5 MB, es sincrono (congela la pantalla al crecer), solo guarda texto y
 * no tiene transacciones. IndexedDB si las tiene, y son las que garantizan que
 * una produccion no descuente el inventario a medias (Req. 1.4).
 */

import Dexie, { type Table } from 'dexie';
import { ahoraIso } from '@/domain/dates';
import {
  VERSION_ESQUEMA,
  type Settings,
  type Ingredient,
  type Purchase,
  type PurchaseItem,
  type Recipe,
  type RecipeItem,
  type MenuTemplate,
  type MenuTemplateItem,
  type PlannedItem,
  type Production,
  type ProductionItem,
  type Sale,
  type SaleDisposition,
  type InventoryMovement,
  type ShoppingCheck,
  type Order,
  type CashMovement,
} from './types';

export const NOMBRE_BASE_DATOS = 'control-negocio-comida';

/** Version del esquema que soporta esta compilacion de la aplicacion. */
export const VERSION_ESQUEMA_ACTUAL = VERSION_ESQUEMA;

export class BaseDatos extends Dexie {
  settings!: Table<Settings, number>;
  ingredients!: Table<Ingredient, string>;
  purchases!: Table<Purchase, string>;
  purchaseItems!: Table<PurchaseItem, string>;
  recipes!: Table<Recipe, string>;
  recipeItems!: Table<RecipeItem, string>;
  menuTemplates!: Table<MenuTemplate, string>;
  menuTemplateItems!: Table<MenuTemplateItem, string>;
  plannedItems!: Table<PlannedItem, string>;
  productions!: Table<Production, string>;
  productionItems!: Table<ProductionItem, string>;
  sales!: Table<Sale, string>;
  saleDispositions!: Table<SaleDisposition, string>;
  inventoryMovements!: Table<InventoryMovement, string>;
  shoppingChecks!: Table<ShoppingCheck, string>;
  orders!: Table<Order, string>;
  cashMovements!: Table<CashMovement, string>;

  constructor() {
    super(NOMBRE_BASE_DATOS);

    /**
     * Version 1 del esquema.
     *
     * Los indices estan elegidos para las consultas mas frecuentes, que son
     * las tres del dashboard:
     *   plannedItems por fechaVenta -> "hoy entregas"
     *   plannedItems por fechaPrep  -> "hoy debes preparar"
     *   productions  por fechaPrep  -> estado de la produccion del dia
     *
     * Al agregar una version nueva NUNCA se modifica esta: se encadena otra
     * con .version(2) y su .upgrade() correspondiente (Req. 1.5).
     */
    this.version(1).stores({
      settings: 'id',
      ingredients: 'id, nombre, categoria, activo, dimension',
      purchases: 'id, fecha',
      purchaseItems: 'id, purchaseId, ingredientId',
      recipes: 'id, nombre, categoria, activo',
      recipeItems: 'id, recipeId, ingredientId',
      menuTemplates: 'id, activo',
      menuTemplateItems: 'id, templateId, diaSemana',
      plannedItems: 'id, fechaVenta, fechaPrep, recipeId, [fechaVenta+recipeId]',
      productions: 'id, plannedItemId, fechaPrep, fechaVenta, estado',
      productionItems: 'id, productionId, ingredientId',
      sales: 'id, fecha, recipeId, plannedItemId',
      saleDispositions: 'id, plannedItemId, tipo',
      inventoryMovements: 'id, ingredientId, fecha, tipo, [ingredientId+fecha]',
      shoppingChecks: 'id, ingredientId, periodoClave',
    });

    /**
     * Version 2: se agrega la tabla de pedidos. NADA MAS.
     *
     * Dexie hereda el esquema de la version anterior, asi que las 15 tablas de
     * arriba quedan intactas con todos sus datos: subir de version abre una
     * transaccion que crea la store nueva y no toca las existentes. No hace
     * falta `.upgrade()` porque no hay ningun dato viejo que transformar.
     *
     * El numero TIENE que subir. IndexedDB solo crea stores dentro de una
     * transaccion de cambio de version; si se agregara la tabla dentro del
     * bloque de la version 1, en un dispositivo que ya tiene la base creada la
     * store nunca apareceria y la aplicacion fallaria al abrir.
     *
     * El indice principal es `fechaEntrega`, porque la pantalla de pedidos
     * siempre pregunta "que entrego este dia". `clienteNombre` esta indexado
     * para poder buscar por persona.
     */
    this.version(2).stores({
      orders: 'id, fechaEntrega, recipeId, plannedItemId, clienteNombre',
    });

    /**
     * Version 3: se agrega la tabla de movimientos de caja. NADA MAS.
     *
     * Igual que la version 2: Dexie hereda las 16 tablas anteriores con todos sus
     * datos, y no hace falta `.upgrade()` porque no hay ningun registro existente
     * que transformar. Agregar una store es la clase de cambio de esquema mas
     * segura que existe.
     *
     * El campo `orders.fechaPago` que se agrego junto con esta tabla NO aparece
     * aqui a proposito: no esta indexado, asi que Dexie no necesita saber de el.
     * Los pedidos anteriores simplemente no lo traen y se leen como no pagados.
     *
     * `fecha` esta indexado porque la pantalla de Caja siempre pregunta por un
     * periodo, y `tipo` para poder separar el dinero de ella del del negocio sin
     * recorrer toda la tabla.
     */
    this.version(3).stores({
      cashMovements: 'id, fecha, tipo',
    });
  }
}

export const db = new BaseDatos();

// ---------------------------------------------------------------------------
// Configuracion
// ---------------------------------------------------------------------------

/**
 * Valores predeterminados de configuracion.
 *
 * Es un objeto congelado y sin marca de tiempo a proposito: se devuelve como
 * respaldo en las lecturas y debe ser siempre el mismo valor, para que una
 * consulta reactiva no crea que cambio y vuelva a dibujar la pantalla.
 */
export const SETTINGS_PREDETERMINADOS: Readonly<Settings> = Object.freeze({
  id: 1 as const,
  schemaVersion: VERSION_ESQUEMA,
  moneda: 'MXN',
  diasAnticipacionDefault: 1,
  primerDiaSemana: 1 as const,
  ultimoRespaldoEn: null,
  diasAvisoRespaldo: 7,
  almacenamientoPersistente: null,
  creadoEn: '',
});

export function settingsIniciales(): Settings {
  return { ...SETTINGS_PREDETERMINADOS, creadoEn: ahoraIso() };
}

/**
 * LECTURA PURA de la configuracion. Nunca escribe.
 *
 * Esta es la unica version apta para `useLiveQuery`.
 *
 * Motivo: Dexie ejecuta las consultas reactivas dentro de una transaccion de
 * solo lectura y prohibe escribir en ellas, porque una consulta que se
 * actualiza sola y ademas escribe se invalidaria a si misma sin parar.
 *
 * Si el registro todavia no existe, devuelve los valores predeterminados en
 * memoria en lugar de crearlo. Crearlo es tarea del arranque.
 */
export async function leerSettings(): Promise<Settings> {
  return (await db.settings.get(1)) ?? SETTINGS_PREDETERMINADOS;
}

/**
 * Crea el registro unico de configuracion si no existe. ESCRIBE.
 *
 * Se llama una sola vez al arrancar la aplicacion, nunca desde una consulta
 * reactiva. Es idempotente, asi que repetir la llamada no causa dano.
 */
export async function asegurarSettings(): Promise<Settings> {
  const existente = await db.settings.get(1);
  if (existente !== undefined) return existente;

  const nuevos = settingsIniciales();
  await db.settings.put(nuevos);
  return nuevos;
}

/** ESCRIBE. Solo desde manejadores de eventos, nunca desde una consulta reactiva. */
export async function actualizarSettings(cambios: Partial<Omit<Settings, 'id'>>): Promise<void> {
  await asegurarSettings();
  await db.settings.update(1, cambios);
}

// ---------------------------------------------------------------------------
// Diagnostico
// ---------------------------------------------------------------------------

/** Cuenta los registros de todas las tablas. Para el resumen de respaldo. */
export async function contarRegistros(): Promise<Record<string, number>> {
  const conteos: Record<string, number> = {};
  await Promise.all(
    db.tables.map(async (tabla) => {
      conteos[tabla.name] = await tabla.count();
    }),
  );
  return conteos;
}

/** Total de registros de negocio, excluyendo la configuracion. */
export async function totalRegistrosNegocio(): Promise<number> {
  const conteos = await contarRegistros();
  return Object.entries(conteos)
    .filter(([nombre]) => nombre !== 'settings')
    .reduce((suma, [, n]) => suma + n, 0);
}

export async function hayDatosCapturados(): Promise<boolean> {
  return (await totalRegistrosNegocio()) > 0;
}
